<template>
  <div class="ai-staff-members">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t("__aiStaffMembers") || "AI 客服人員管理" }}</h2>

      <!-- 篩選和搜尋區 -->
      <div class="d-flex flex-wrap align-items-center justify-content-start justify-content-md-between">
        <!-- 狀態篩選 -->
        <div class="device-select me-2 mb-2">
          <select v-model="filterStatus" @change="loadStaffMembers" class="form-select rounded-pill me-2">
            <option :value="null">{{ $t("__all") }}</option>
            <option :value="true">{{ $t("__aiStaffStatusActive") }}</option>
            <option :value="false">{{ $t("__aiStaffStatusInactive") }}</option>
          </select>
        </div>

        <!-- 機構篩選 -->
        <div class="d-flex align-items-center me-2 mb-2">
          <div class="device-report-select">
            <select v-model="searchAgency" @change="loadStaffMembers" class="form-select rounded-pill me-2">
              <option :value="null">{{ $t("__all") + " " + $t('__agency') }}</option>
              <option v-for="item in allAgencys" :value="item.id" :key="'agent'+ item.id">
                {{ item.name + ' / ' + item.codename }}
              </option>
            </select>
          </div>
        </div>

        <!-- 搜尋框 -->
        <div class="search-box me-2 mb-2">
          <div class="input-group">
            <input
              v-model="searchText"
              @input="debounceSearch"
              type="text"
              class="form-control rounded-pill"
              :placeholder="$t('__aiStaffSearchPlaceholder')"
            >
          </div>
        </div>

        <!-- 操作按鈕群 -->
        <div class="ms-auto mb-2">
          <button
            @click="syncCache"
            type="button"
            class="btn btn-secondary rounded-pill me-2"
            :disabled="syncing"
          >
            <i class="bi bi-arrow-clockwise me-2" :class="{'spin': syncing}"></i>
            {{ syncing ? $t('__aiStaffSyncing') : $t('__aiStaffSyncCache') }}
          </button>

          <button
            @click="showModal()"
            type="button"
            class="btn btn-primary rounded-pill"
          >
            <i class="bi bi-plus-circle me-2"></i>{{ $t("__add") || "新增客服" }}
          </button>
        </div>
      </div>


      <!-- 批量操作 -->
      <div v-if="selectedIds.length > 0" class="alert alert-info d-flex align-items-center">
        <span class="me-3">{{ $t('__aiStaffSelected', { count: selectedIds.length }) }}</span>
        <button @click="batchUpdate(true)" class="btn btn-sm btn-success me-2">{{ $t('__aiStaffBatchEnable') }}</button>
        <button @click="batchUpdate(false)" class="btn btn-sm btn-secondary me-2">{{ $t('__aiStaffBatchDisable') }}</button>
        <button @click="clearSelection" class="btn btn-sm btn-outline-secondary">{{ $t('__aiStaffClearSelection') }}</button>
      </div>

      <!-- 資料表格 -->
      <div class="card">
        <div class="card-body">
          <b-table
            :responsive="$store.state.windowWidth > $store.state.breakpoints.sm"
            :stacked="$store.state.windowWidth <= $store.state.breakpoints.sm"
            striped
            borderless
            no-provider-filtering
            head-variant="light"
            :items="staffMembers"
            :fields="fields"
            :current-page="currentPage"
            :per-page="perPage"
            ref="staffTable"
          >
            <template v-slot:head()="scope">
              <div class="text-nowrap">
                {{ scope.label }}
              </div>
            </template>

            <template v-slot:cell(line_user_id)="data">
              <code>{{ data.item.line_user_id }}</code>
            </template>

            <template v-slot:cell(line_display_name)="data">
              {{ data.item.line_display_name || '-' }}
            </template>

            <template v-slot:cell(staff_name)="data">
              {{ data.item.staff_name || '-' }}
            </template>

            <template v-slot:cell(staff_code)="data">
              {{ data.item.staff_code || '-' }}
            </template>

            <template v-slot:cell(agency)="data">
              <span v-if="data.item.agency">{{ data.item.agency.name }}</span>
              <span v-else class="badge bg-info">{{ $t('__aiStaffGlobal') }}</span>
            </template>

            <template v-slot:cell(is_active)="data">
              <span v-if="data.item.is_active" class="badge bg-success">{{ $t('__enabled') }}</span>
              <span v-else class="badge bg-secondary">{{ $t('__disabled') }}</span>
            </template>

            <template v-slot:cell(created_at)="data">
              <small>{{ formatDate(data.item.created_at) }}</small>
            </template>

            <template v-slot:cell(actions)="data">
              <div class="d-flex">
                <button
                  @click="showModal(data.item)"
                  type="button"
                  class="btn btn-link px-1 text-dark"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__edit')"
                >
                  <i class="bi bi-pencil mx-1 fs-5"></i>
                </button>
                <button
                  @click="toggleStatus(data.item)"
                  type="button"
                  class="btn btn-link px-1"
                  :class="data.item.is_active ? 'text-warning' : 'text-success'"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="data.item.is_active ? $t('__disabled') : $t('__enabled')"
                >
                  <i :class="data.item.is_active ? 'bi bi-pause fs-5' : 'bi bi-play fs-5'"></i>
                </button>
                <button
                  @click="confirmDelete(data.item)"
                  type="button"
                  class="btn btn-link px-1 text-danger"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__delete')"
                >
                  <i class="bi bi-trash fs-5"></i>
                </button>
              </div>
            </template>
          </b-table>
        </div>
      </div>


      <!-- 無資料提示 -->
      <div v-if="!loading && staffMembers && staffMembers.length === 0" class="text-center py-4">
        <i class="bi bi-inbox fs-1 text-muted"></i>
        <p class="text-muted mt-2">{{ $t('__aiStaffNoData') }}</p>
      </div>

      <!-- 載入中 -->
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ $t('__aiStaffLoading') }}</span>
        </div>
      </div>

      <!-- 分頁資訊 -->
      <div class="row align-items-center mt-3" v-if="totalRows > 0">
        <div class="col-sm col-12 text-right text-sm-left">
          {{ $t('__paginationInfo', {
            startNub: currentPage == 1 ? 1 : perPage * (currentPage - 1) + 1,
            entNub: currentPage == 1
              ? totalRows > perPage
                ? perPage
                : totalRows
              : perPage * (currentPage - 1) + perPage > totalRows
              ? totalRows
              : perPage * (currentPage - 1) + perPage ,
            sum: totalRows
            })
          }}
        </div>
        <div class="col-sm mt-3 mt-sm-0 col-12">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            size="sm"
            align="right"
            limit="5"
            class="my-0 pagination-primary"
            @change="changePage"
          />
        </div>
      </div>

      <!-- 暫停群組管理 -->
      <div class="row mt-4" v-if="pausedGroups && pausedGroups.length > 0">
        <div class="col-12">
          <h5 class="mb-3">{{ $t('__aiStaffPausedGroups') }}</h5>
          <div class="table-responsive">
            <table class="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>{{ $t('__aiStaffGroupId') }}</th>
                  <th>{{ $t('__aiStaffPausedBy') }}</th>
                  <th>{{ $t('__aiStaffRemainingTime') }}</th>
                  <th width="100">{{ $t('__operation') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="group in pausedGroups" :key="group.group_id">
                  <td><code>{{ group.group_id }}</code></td>
                  <td>{{ group.staff_name || group.staff_id }}</td>
                  <td>
                    <countdown :end-time="group.resume_at"></countdown>
                  </td>
                  <td>
                    <button
                      @click="resumeGroup(group.group_id)"
                      class="btn btn-sm btn-success"
                    >
                      {{ $t('__aiStaffResume') }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增/編輯 Modal -->
    <div
      class="modal fade cms-style"
      id="staffMemberModal"
      tabindex="-1"
      ref="staffMemberModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <form @submit.prevent="saveStaffMember">
            <div class="modal-header">
              <h5 class="modal-title fw-medium">
                {{ editMode ? $t('__aiStaffEditTitle') : $t('__aiStaffAddTitle') }}
              </h5>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">{{ $t('__aiStaffLineUserIdLabel') }} <span class="text-danger">*</span></label>
            <input
              v-model="form.line_user_id"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.line_user_id }"
              :placeholder="$t('__aiStaffLineUserIdPlaceholder')"
              :disabled="editMode"
              required
            >
            <div v-if="errors.line_user_id" class="invalid-feedback">
              {{ errors.line_user_id[0] }}
            </div>
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label">{{ $t('__aiStaffLineDisplayNameLabel') }}</label>
            <input
              v-model="form.line_display_name"
              type="text"
              class="form-control"
              :placeholder="$t('__aiStaffLineDisplayNamePlaceholder')"
            >
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label">{{ $t('__aiStaffNameLabel') }}</label>
            <input
              v-model="form.staff_name"
              type="text"
              class="form-control"
              :placeholder="$t('__aiStaffNamePlaceholder')"
            >
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label">{{ $t('__aiStaffCodeLabel') }}</label>
            <input
              v-model="form.staff_code"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.staff_code }"
              :placeholder="$t('__aiStaffCodePlaceholder')"
            >
            <div v-if="errors.staff_code" class="invalid-feedback">
              {{ errors.staff_code[0] }}
            </div>
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label">{{ $t('__aiStaffAgencyLabel') }}</label>
            <select v-model="form.agency_id" class="form-select">
              <option :value="null">{{ $t('__aiStaffGlobalAgency') }}</option>
              <option v-for="agency in allAgencys" :value="agency.id" :key="agency.id">
                {{ agency.name }}
              </option>
            </select>
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label">{{ $t('__aiStaffStatusLabel') }}</label>
            <div class="form-check form-switch">
              <input
                v-model="form.is_active"
                type="checkbox"
                class="form-check-input"
                id="isActiveSwitch"
              >
              <label class="form-check-label" for="isActiveSwitch">
                {{ form.is_active ? $t('__enabled') : $t('__disabled') }}
              </label>
            </div>
          </div>

              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                {{ $t('__cancel') }}
              </button>
              <button type="submit" class="btn btn-primary">
                {{ $t('__save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- 匯入 Modal -->
    <div
      class="modal fade cms-style"
      id="importModal"
      tabindex="-1"
      ref="importModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <form @submit.prevent="importFromLine">
            <div class="modal-header">
              <h5 class="modal-title fw-medium">{{ $t('__aiStaffImportTitle') }}</h5>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
      <div class="mb-3">
        <label class="form-label">{{ $t('__aiStaffImportSelectAgency') }}</label>
        <select v-model="importAgencyId" class="form-select">
          <option :value="null">{{ $t('__aiStaffGlobalAgency') }}</option>
          <option v-for="agency in allAgencys" :value="agency.id" :key="agency.id">
            {{ agency.name }}
          </option>
        </select>
      </div>

      <div class="mb-3">
        <input
          v-model="lineSearchText"
          type="text"
          class="form-control"
          :placeholder="$t('__aiStaffImportSearch')"
        >
      </div>

      <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
        <table class="table table-sm">
          <thead>
            <tr>
              <th width="40">
                <input type="checkbox" @change="toggleLineSelectAll" class="form-check-input">
              </th>
              <th>{{ $t('__aiStaffImportUserId') }}</th>
              <th>{{ $t('__aiStaffImportDisplayName') }}</th>
              <th>{{ $t('__aiStaffImportStatus') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="friend in filteredLineFriends" :key="friend.userId">
              <td>
                <input
                  type="checkbox"
                  :value="friend"
                  v-model="selectedLineFriends"
                  class="form-check-input"
                  :disabled="friend.exists"
                >
              </td>
              <td><code>{{ friend.userId }}</code></td>
              <td>{{ friend.displayName }}</td>
              <td>
                <span v-if="friend.exists" class="badge bg-secondary">{{ $t('__aiStaffImportExists') }}</span>
                <span v-else class="badge bg-success">{{ $t('__aiStaffImportAvailable') }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

              <div v-if="selectedLineFriends.length > 0" class="alert alert-info mt-3">
                {{ $t('__aiStaffImportSelected', { count: selectedLineFriends.length }) }}
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                {{ $t('__cancel') }}
              </button>
              <button type="submit" class="btn btn-primary">
                {{ $t('__aiStaffImportButton') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BTable, BPagination } from 'bootstrap-vue'
import { Modal } from 'bootstrap'
import { aiStaffMembers } from '@/http/api/aiStaffMembers.js'
import { agency } from '@/http/api/agency.js'
import { mapState } from 'vuex'
import moment from 'moment'

export default {
  name: 'AiStaffMembers',
  components: {
    BTable,
    BPagination
  },
  data() {
    return {
      staffMembers: [],
      allAgencys: [],
      pausedGroups: [],
      loading: false,
      syncing: false,
      editMode: false,
      staffModal: null,
      importModal: null,
      searchText: '',
      searchAgency: null,
      filterStatus: null,
      selectedIds: [],
      currentPage: 1,
      totalPages: 1,
      totalRows: 0,
      perPage: 20,
      form: {
        id: null,
        line_user_id: '',
        line_display_name: '',
        staff_name: '',
        staff_code: '',
        agency_id: null,
        is_active: true
      },
      errors: {},
      debounceTimer: null,

      // 匯入相關
      importAgencyId: null,
      lineFriends: [],
      selectedLineFriends: [],
      lineSearchText: ''
    }
  },
  computed: {
    ...mapState(['token', 'userInfo']),
    permissions() {
      let permissions = null
      if (this.userInfo) {
        permissions = this.userInfo.permissions
      }
      return permissions
    },
    fields() {
      return [
        {
          key: 'line_user_id',
          label: this.$t('__aiStaffLineUserId'),
          sortable: true,
          class: 'align-middle'
        },
        {
          key: 'line_display_name',
          label: this.$t('__aiStaffLineDisplayName'),
          sortable: true,
          class: 'align-middle'
        },
        {
          key: 'staff_name',
          label: this.$t('__aiStaffName'),
          sortable: true,
          class: 'align-middle'
        },
        {
          key: 'staff_code',
          label: this.$t('__aiStaffCode'),
          sortable: true,
          class: 'align-middle'
        },
        {
          key: 'agency',
          label: this.$t('__agency'),
          sortable: true,
          class: 'align-middle'
        },
        {
          key: 'is_active',
          label: this.$t('__status'),
          sortable: true,
          class: 'align-middle'
        },
        {
          key: 'created_at',
          label: this.$t('__aiStaffCreatedAt'),
          sortable: true,
          class: 'align-middle'
        },
        {
          key: 'actions',
          label: this.$t('__operation'),
          class: 'align-middle'
        }
      ]
    },
    filteredLineFriends() {
      if (!this.lineSearchText) return this.lineFriends

      const search = this.lineSearchText.toLowerCase()
      return this.lineFriends.filter(friend =>
        friend.userId.toLowerCase().includes(search) ||
        (friend.displayName && friend.displayName.toLowerCase().includes(search))
      )
    }
  },
  mounted() {
    // 確保 token 已經載入
    this.$store.commit('getToken')
    this.loadAgencies()
    this.loadStaffMembers()
    // this.checkPausedGroups()  // 暫時註解

    // 每30秒更新暫停群組狀態
    this.pauseCheckInterval = setInterval(() => {
      this.checkPausedGroups()
    }, 30000)
  },
  beforeDestroy() {
    if (this.pauseCheckInterval) {
      clearInterval(this.pauseCheckInterval)
    }
  },
  methods: {
    async loadAgencies() {
      try {
        const response = await agency.get('', this.token)
        this.allAgencys = response.data.data || response.data
      } catch (error) {
        console.error(this.$t('__aiStaffLoadAgenciesFailed'), error)
      }
    },

    async loadStaffMembers() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage
        }

        if (this.searchText) params.search = this.searchText
        if (this.searchAgency) params.agency_id = this.searchAgency
        if (this.filterStatus !== null) params.is_active = this.filterStatus

        const response = await aiStaffMembers.get(params, this.token)
        this.staffMembers = response.data.data || []
        this.currentPage = response.data.current_page || 1
        this.totalPages = response.data.last_page || 1
        this.totalRows = response.data.total || 0

        // 檢查 Redis 同步狀態 (暫時註解，可能導致太多請求)
        // for (let staff of this.staffMembers) {
        //   this.checkRedisSync(staff)
        // }
      } catch (error) {
        console.error(this.$t('__aiStaffLoadFailed'), error)
      } finally {
        this.loading = false
      }
    },


    async checkRedisSync(staff) {
      try {
        const response = await aiStaffMembers.getOne(staff.id, this.token)
        staff.redis_synced = response.data.redis_status === 'synced'
      } catch (error) {
        staff.redis_synced = false
      }
    },

    async checkPausedGroups() {
      // 這裡需要實作取得所有暫停群組的 API
      // 目前先用假資料示範
      this.pausedGroups = []
    },

    debounceSearch() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1
        this.loadStaffMembers()
      }, 500)
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.loadStaffMembers()
    },

    showModal(item = null) {
      if (item) {
        this.editMode = true
        this.form = { ...item }
      } else {
        this.editMode = false
        this.resetForm()
      }
      // 使用 Bootstrap 5 Modal 開啟
      this.staffModal = new Modal(this.$refs.staffMemberModal)
      this.$nextTick(() => {
        this.staffModal.show()
      })
    },

    resetForm() {
      this.form = {
        id: null,
        line_user_id: '',
        line_display_name: '',
        staff_name: '',
        staff_code: '',
        agency_id: null,
        is_active: true
      }
      this.errors = {}
    },

    async saveStaffMember(event) {
      if (event) event.preventDefault()

      try {
        if (this.editMode) {
          await aiStaffMembers.update(this.form.id, this.form, this.token)
          alert(this.$t('__aiStaffUpdateSuccess'))
        } else {
          await aiStaffMembers.create(this.form, this.token)
          alert(this.$t('__aiStaffCreateSuccess'))
        }

        // 關閉 Modal
        if (this.staffModal) {
          this.staffModal.hide()
        }

        this.loadStaffMembers()
      } catch (error) {
        console.error('Save error:', error)
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors
        } else {
          alert(this.editMode ? this.$t('__aiStaffUpdateFailed') : this.$t('__aiStaffCreateFailed'))
        }
      }
    },

    async toggleStatus(item) {
      try {
        await aiStaffMembers.update(item.id, {
          is_active: !item.is_active
        }, this.token)

        item.is_active = !item.is_active
        alert(item.is_active ? this.$t('__aiStaffEnabledSuccess') : this.$t('__aiStaffDisabledSuccess'))
      } catch (error) {
        console.error('Toggle status error:', error)
        alert(this.$t('__aiStaffToggleFailed'))
      }
    },

    confirmDelete(item) {
      const name = item.staff_name || item.line_display_name || item.line_user_id
      if (confirm(this.$t('__aiStaffDeleteConfirm', { name }))) {
        this.deleteStaffMember(item)
      }
    },

    async deleteStaffMember(item) {
      try {
        await aiStaffMembers.delete(item.id, this.token)

        alert(this.$t('__aiStaffDeleteSuccess'))

        this.loadStaffMembers()
      } catch (error) {
        alert(this.$t('__aiStaffDeleteFailed'))
      }
    },

    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedIds = this.staffMembers.map(item => item.id)
      } else {
        this.selectedIds = []
      }
    },

    clearSelection() {
      this.selectedIds = []
    },

    async batchUpdate(isActive) {
      const action = isActive ? this.$t('__aiStaffBatchEnable') : this.$t('__aiStaffBatchDisable')
      if (!confirm(this.$t('__aiStaffBatchConfirm', { action, count: this.selectedIds.length }))) {
        return
      }

      try {
        const response = await aiStaffMembers.batchUpdateStatus({
          ids: this.selectedIds,
          is_active: isActive
        }, this.token)

        alert(response.data.message || this.$t('__aiStaffBatchSuccess'))

        this.selectedIds = []
        this.loadStaffMembers()
      } catch (error) {
        alert(this.$t('__aiStaffBatchFailed'))
      }
    },

    async syncCache() {
      this.syncing = true
      try {
        const response = await aiStaffMembers.syncCache(this.token)

        alert(response.data.message || this.$t('__aiStaffSyncSuccess'))

        // 重新檢查同步狀態 (暫時註解)
        // for (let staff of this.staffMembers) {
        //   this.checkRedisSync(staff)
        // }
      } catch (error) {
        alert(this.$t('__aiStaffSyncFailed'))
      } finally {
        this.syncing = false
      }
    },

    async resumeGroup(groupId) {
      try {
        await aiStaffMembers.setPauseControl({
          group_id: groupId,
          action: 'resume'
        }, this.token)

        alert(this.$t('__aiStaffResumeSuccess'))

        this.checkPausedGroups()
      } catch (error) {
        alert(this.$t('__aiStaffResumeFailed'))
      }
    },

    showImportModal() {
      // 載入 Line 好友清單（需要實作 API）
      this.importModal = new Modal(this.$refs.importModal)
      this.$nextTick(() => {
        this.importModal.show()
      })
    },

    toggleLineSelectAll(event) {
      if (event.target.checked) {
        this.selectedLineFriends = this.filteredLineFriends.filter(f => !f.exists)
      } else {
        this.selectedLineFriends = []
      }
    },

    async importFromLine(event) {
      if (event) event.preventDefault()

      if (this.selectedLineFriends.length === 0) {
        alert(this.$t('__aiStaffImportSelectFriends'))
        return
      }

      try {
        const response = await aiStaffMembers.importFromLine({
          line_users: this.selectedLineFriends,
          agency_id: this.importAgencyId
        }, this.token)

        alert(this.$t('__aiStaffImportSuccess', {
          imported: response.data.imported || 0,
          skipped: response.data.skipped || 0
        }))

        // 關閉匯入 Modal
        if (this.importModal) {
          this.importModal.hide()
        }
        this.selectedLineFriends = []
        this.loadStaffMembers()
      } catch (error) {
        alert(this.$t('__aiStaffImportFailed'))
      }
    },

    formatDate(date) {
      return moment(date).format('YYYY-MM-DD HH:mm')
    }
  }
}
</script>

<style scoped>
.ai-staff-members {
  min-height: calc(100vh - 100px);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.search-box .form-control {
  min-width: 300px;
}

.table td {
  vertical-align: middle;
}

code {
  padding: 2px 4px;
  background-color: #f4f4f4;
  border-radius: 3px;
  font-size: 0.875rem;
}
</style>
